import type {
  DatesSetArg,
  EventClickArg,
  EventDropArg,
  MoreLinkArg
} from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin, { type DateClickArg } from "@fullcalendar/interaction"
import listPlugin from "@fullcalendar/list"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import { Button, Flex, Typography } from "antd"
import dayjs from "dayjs"
import React, { memo, useEffect, useMemo, useRef, useState } from "react"

import type {
  CalendarFilters as CalendarFiltersType,
  CalendarModalData
} from "../../redux/calendar.types.ts"
import { type Event } from "../../types/types.ts"
import { parseCalendarFocusDate } from "../../helpers/calendar-deep-link.ts"
import { eventMatchesSearch } from "../helpers/event-matches-search.ts"
import { resolveNewEventSlot } from "../helpers/new-event-slot.ts"
import { createStartAlignedEventDrag } from "../helpers/start-aligned-event-drag.ts"
import { transformEventsForCalendar } from "../helpers/transform-events-for-calendar"
import { CalendarAvailability } from "./CalendarAvailability/CalendarAvailability.tsx"
import { CalendarEventCell } from "./CalendarEventCell/CalendarEventCell"
import { CalendarFilters } from "./CalendarFilters/CalendarFilters"
import { CalendarHeader } from "./CalendarHeader"
import { CalendarNavigation } from "./CalendarNavigation"
import { CalendarSidebar } from "./CalendarSidebar/CalendarSidebar.tsx"
import "./calendar.less"

const { Text } = Typography

interface CalendarProps {
  timezone: string
  focusDate?: string | null
  filters: CalendarFiltersType
  isMobile: boolean
  events: Event[]
  userId?: string
  //
  createAllowed?: boolean
  editAllowed?: boolean
  viewAllAllowed?: boolean
  //
  onSelectEvent: (id: string) => void
  onSetModalData: (data: CalendarModalData | null) => void
  onFetchEvents: ({ start, end }: Partial<DatesSetArg>) => void
  onFilterChange: (filter: Record<string, unknown>) => void
  onSetMoreInfo: (args: MoreLinkArg) => void
  onDropEvent: (arg: EventDropArg) => void
}

export const Calendar = memo(
  ({
    timezone,
    focusDate,
    filters,
    isMobile,
    userId,
    createAllowed,
    editAllowed,
    viewAllAllowed,
    events: apiEvents = [],
    onSelectEvent,
    onSetModalData,
    onFetchEvents,
    onFilterChange,
    onSetMoreInfo,
    onDropEvent
  }: CalendarProps) => {
    const isFirstRender = useRef(true)
    const calendarRef = useRef<FullCalendar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const gridWrapRef = useRef<HTMLDivElement | null>(null)
    const startAlignedEventDrag = useMemo(createStartAlignedEventDrag, [])
    const getAPI = () => calendarRef?.current?.getApi()

    const [searchQuery, setSearchQuery] = useState("")

    const [nowLine, setNowLine] = useState({
      visible: false,
      top: 0,
      left: 0,
      width: 0,
      axisWidth: 0
    })

    // Client-side only, over whatever the server already returned for the
    // visible range — typing a search must never fire a request per
    // keystroke (the separate autocomplete box in CalendarFiltersSearch
    // still hits the server, since it searches beyond the loaded range).
    const searchedApiEvents = useMemo(
      () => apiEvents.filter((event) => eventMatchesSearch(event, searchQuery)),
      [apiEvents, searchQuery]
    )

    const events = transformEventsForCalendar(searchedApiEvents)
    const hasAllDayEvents = events.some((event) => event.allDay)
    const [currentView, setCurrentView] = useState(
      isMobile ? "listWeek" : "timeGridWeek"
    )
    const isAvailabilityView = currentView === "availability"

    // Availability's own date, driven directly by every action that changes
    // it rather than read back from FullCalendar afterward. Reading
    // `getAPI()?.getDate()` only reflects a `gotoDate()` call once something
    // re-renders this component, and while Availability is showing, the
    // hidden underlying view's `datesSet` — the thing that normally causes
    // that re-render, via the events refetch — often doesn't fire a change
    // at all: a one-day step easily lands inside the same week/month the
    // hidden view was already showing, so nothing about its own fetch range
    // changes. That silently broke Prev/Next and the mini-month while on
    // Availability. Explicit state sidesteps the question entirely.
    const [availabilityDate, setAvailabilityDate] = useState<Date | null>(
      null
    )

    const isFiltering =
      !!searchQuery.trim() ||
      filters.type !== null ||
      filters.entity_type !== null ||
      !!filters.participants?.length
    const showEmptyState = isFiltering && events.length === 0

    const handleClearFilters = () => {
      setSearchQuery("")
      onFilterChange({ type: null, entity_type: null, participants: null })
    }

    const now = dayjs().tz(timezone).format()

    const handleDateClick = (arg: DateClickArg) => {
      onSetModalData(resolveNewEventSlot(arg.dateStr, timezone, arg.allDay))
    }

    const handleEventClick = (clickInfo: EventClickArg) => {
      const event = clickInfo?.event
      const id = event?.id || event?.extendedProps?.id
      onSelectEvent(id)
    }

    // Availability isn't a real FullCalendar view (see the render below), so
    // its own Prev/Next always steps by exactly one day — `.prev()`/`.next()`
    // would otherwise step by whatever duration the hidden underlying view
    // happens to have (a week, a month), which is wrong for a single day.
    const onPrevClick = () => {
      const calendarAPI = getAPI()
      if (!calendarAPI) return

      if (currentView === "availability") {
        const next = dayjs(availabilityDate ?? calendarAPI.getDate())
          .tz(timezone)
          .subtract(1, "day")
          .toDate()
        setAvailabilityDate(next)
        calendarAPI.gotoDate(next)
      } else {
        calendarAPI.prev()
      }
    }

    const onNextClick = () => {
      const calendarAPI = getAPI()
      if (!calendarAPI) return

      if (currentView === "availability") {
        const next = dayjs(availabilityDate ?? calendarAPI.getDate())
          .tz(timezone)
          .add(1, "day")
          .toDate()
        setAvailabilityDate(next)
        calendarAPI.gotoDate(next)
      } else {
        calendarAPI.next()
      }
    }

    const onTodayClick = () => {
      const calendarAPI = getAPI()

      if (calendarAPI) {
        calendarAPI.today()
        calendarAPI.gotoDate(calendarAPI.getDate())
      }
      if (currentView === "availability") {
        setAvailabilityDate(dayjs().tz(timezone).toDate())
      }
    }

    // "availability" has no matching FullCalendar view definition — it's a
    // sibling component swapped in over the same slot (see the render below)
    // while FullCalendar itself stays mounted, unchanged, so its own date and
    // view survive switching away and back.
    const onViewChange = (view: string) => {
      const calendarAPI = getAPI()
      setCurrentView(view)

      if (view === "availability") {
        setAvailabilityDate(
          calendarAPI?.getDate() ?? dayjs().tz(timezone).toDate()
        )
      } else if (calendarAPI) {
        calendarAPI.changeView(view)
      }
    }

    // Mobile and desktop use disjoint view sets (list* vs timeGrid/dayGrid) —
    // if the breakpoint is crossed after mount, the view has to move with it.
    // The initial value is already right (see the `useState` above), so this
    // only needs to act on a later change, not repeat the same `changeView`
    // FullCalendar hasn't even mounted for yet on the first pass.
    const isFirstViewSync = useRef(true)
    useEffect(() => {
      if (isFirstViewSync.current) {
        isFirstViewSync.current = false
        return
      }
      onViewChange(isMobile ? "listWeek" : "timeGridWeek")
    }, [isMobile])

    const handleMoreLinkClick = (info: MoreLinkArg): any => {
      info.jsEvent.preventDefault()
      onSetMoreInfo(info)
      return () => null
    }

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      if (timezone) {
        const calendarAPI = getAPI()
        const dateStr = dayjs().tz(timezone).format()

        if (calendarAPI) {
          calendarAPI.gotoDate(dateStr)
          calendarAPI.setOption("now", dateStr)
        }
      }
    }, [timezone])

    // A meeting opened from a Lead names its date in the URL, so the calendar
    // has to land on that page rather than on today.
    useEffect(() => {
      const target = parseCalendarFocusDate(focusDate)
      if (!target) return
      getAPI()?.gotoDate(target)
    }, [focusDate])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return
      const resizeObserver = new ResizeObserver(() => getAPI()?.updateSize())
      resizeObserver.observe(container)
      return () => resizeObserver.disconnect()
    }, [])

    // FullCalendar's own now-indicator line only ever spans one day column —
    // that's a hard limit of how it lays the line out, not a config option.
    // Rather than reimplement its time-to-pixel math (timezone, slot height,
    // scroll position — all already correct there), this mirrors the
    // position of that line (kept in the DOM but invisible, see calendar.less)
    // onto a second, separately-painted line that spans the full grid width.
    useEffect(() => {
      const updateNowLine = () => {
        const wrap = gridWrapRef.current
        const line = wrap?.querySelector(
          ".fc-timegrid-now-indicator-line"
        ) as HTMLElement | null
        const gridEl = wrap?.querySelector(
          ".fc-timegrid-body"
        ) as HTMLElement | null
        const axisEl = wrap?.querySelector(
          ".fc-timegrid-axis-frame"
        ) as HTMLElement | null

        if (!wrap || !line || !gridEl) {
          setNowLine((prev) => (prev.visible ? { ...prev, visible: false } : prev))
          return
        }

        const wrapRect = wrap.getBoundingClientRect()
        const gridRect = gridEl.getBoundingClientRect()
        const axisWidth = axisEl?.getBoundingClientRect()?.width || 0
        const lineTop = line.getBoundingClientRect().top - wrapRect.top

        setNowLine({
          visible: true,
          top: lineTop,
          left: gridRect.left - wrapRect.left,
          width: gridRect.width,
          axisWidth
        })
      }

      updateNowLine()
      // FullCalendar's own DOM can still be mid-mount on the first tick.
      const mountRetry = setTimeout(updateNowLine, 200)

      const scroller = gridWrapRef.current?.querySelector(
        ".fc-timegrid-body"
      )?.closest(".fc-scroller")
      scroller?.addEventListener("scroll", updateNowLine)
      window.addEventListener("resize", updateNowLine)
      // FullCalendar's own indicator re-renders on its own ~minute timer;
      // this just has to catch up promptly after that, not drive the clock.
      const interval = setInterval(updateNowLine, 15000)

      return () => {
        scroller?.removeEventListener("scroll", updateNowLine)
        window.removeEventListener("resize", updateNowLine)
        clearInterval(interval)
        clearTimeout(mountRetry)
      }
    }, [currentView, events.length])

    return (
      // `flex-1 min-h-0` (not `size-full`/`height:100%`): this component is
      // a flex-column sibling of the page's own action bar in CalendarPage,
      // not the sole child of its parent — `height:100%` there would claim
      // the parent's FULL height on top of whatever the action bar already
      // took, overflowing past the shell's bounded content area exactly the
      // way the internal grid below already assumes it can't.
      <Flex
        vertical
        className={`w-full flex-1 min-h-0 ${hasAllDayEvents ? "" : "calendar-no-allday"}`}
        ref={containerRef}
      >
        <Flex vertical gap={12} className={"pb-3"}>
          <CalendarHeader
            createAllowed={createAllowed}
            onSetModalData={onSetModalData}
          />

          <CalendarFilters
            filters={filters}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            onFilterChange={onFilterChange}
            onSelectEvent={onSelectEvent}
            onClearFilters={handleClearFilters}
          />
        </Flex>
        <CalendarNavigation
          getAPI={getAPI}
          isMobile={isMobile}
          currentView={currentView}
          timezone={timezone}
          onViewChange={onViewChange}
          onTodayClick={onTodayClick}
          onPrevClick={onPrevClick}
          onNextClick={onNextClick}
        />

        <Flex gap={16} className={"flex-1 min-h-0"}>
        {/* overflow-hidden + rounded-xl mirror .fc's own clipping (calendar.less)
            so the now-line — a sibling of <FullCalendar>, not a descendant —
            is actually clipped to the calendar's rounded border instead of
            painting past it; without this the line's own box had no boundary
            of its own to stop at. */}
        <div
          className={"relative flex-1 min-h-0 min-w-0 overflow-hidden rounded-xl"}
          ref={gridWrapRef}
        >
          {nowLine.visible && !isAvailabilityView && (
            <>
              <div
                className={"calendar-now-axis-label"}
                style={{
                  top: nowLine.top,
                  left: nowLine.left,
                  width: nowLine.axisWidth
                }}
              >
                {dayjs().tz(timezone).format("h:mm A")}
              </div>
              <div
                className={"calendar-now-line"}
                style={{
                  top: nowLine.top,
                  left: nowLine.left + nowLine.axisWidth,
                  width: nowLine.width - nowLine.axisWidth
                }}
              />
            </>
          )}

          {showEmptyState && !isAvailabilityView && (
            <Flex
              vertical
              align={"center"}
              justify={"center"}
              gap={4}
              className={
                "absolute inset-0 z-10 pointer-events-none pt-24 bg-white/70"
              }
            >
              <Text strong className={"text-base"}>
                No appointments found
              </Text>
              <Text type={"secondary"} className={"text-sm"}>
                Try changing your filters or search.
              </Text>
              <Button
                size={"small"}
                className={"pointer-events-auto mt-1"}
                onClick={handleClearFilters}
              >
                Clear filters
              </Button>
            </Flex>
          )}

          {/* Kept mounted (never conditionally removed) so switching to
              Availability and back doesn't lose FullCalendar's own date/view
              state — only visibility toggles. `display:none` looked like the
              obvious way to do that, but FullCalendar can't lay itself out
              (and silently drops `gotoDate`/`prev`/`next` calls) on a
              display:none element, which broke both Availability's own
              Prev/Next and the mini-month's click-to-navigate while
              Availability was showing. `invisible` keeps it laid out and
              functional; `absolute inset-0` keeps that layout from pushing
              Availability's own content down, and it's the last child so
              Availability still paints on top of it. */}
          <div
            className={
              isAvailabilityView
                ? "invisible absolute inset-0 pointer-events-none"
                : "size-full"
            }
          >
          <FullCalendar
          views={{
            timeGridDay: {
              titleFormat: { year: "numeric", month: "long", day: "numeric" } // "January 8, 2026"
            },
            listDay: {
              titleFormat: { year: "numeric", month: "long", day: "numeric" }, // "January 8, 2026"
              // Unlike the compact grid cards, the agenda has room for the
              // fuller "11:30 AM" (space, uppercase) FullCalendar gives by
              // default — the grid's own `meridiem: "short"` override is
              // deliberately not applied here.
              eventTimeFormat: { hour: "numeric", minute: "2-digit", hour12: true }
            },
            dayGridMonth: {
              titleFormat: { year: "numeric", month: "long" } // "January 2026"
            },
            timeGridWeek: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            },
            timeGridWorkWeek: {
              type: "timeGrid",
              duration: { days: 7 },
              hiddenDays: [0, 6],
              titleFormat: { year: "numeric", month: "short", day: "numeric" }
            },
            listWeek: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" }, // "Jan 6, 2026"
              eventTimeFormat: { hour: "numeric", minute: "2-digit", hour12: true }
            },
            listMonth: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" }, // "Jan 6, 2026"
              eventTimeFormat: { hour: "numeric", minute: "2-digit", hour12: true }
            }
          }}
          // handleWindowResize={false}
          datesSet={onFetchEvents}
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            startAlignedEventDrag.plugin,
            listPlugin
          ]}
          titleFormat={{
            weekday: "short",
            month: "short",
            day: "2-digit"
          }}
          dayHeaderContent={(args) => {
            // Use date directly without time zone conversion
            const date = dayjs.utc(args.date)
            // OR if you need a calendar timezone:
            // const date = dayjs(args.date).tz(args.view.dateEnv.timeZone, true)

            const type = args?.view?.type
            const isToday = args?.isToday

            if (["dayGridMonth"]?.includes(type)) {
              return <Text strong>{date.format("ddd")}</Text>
            }

            // Week/Day headers carry an actual date, so jumping to that
            // day's Day view reads as natural; Month's header row is just
            // weekday names shared across the whole grid, so it stays inert.
            const goToDay = () => {
              const calendarAPI = getAPI()
              if (!calendarAPI) return
              calendarAPI.changeView("timeGridDay", args.date)
              setCurrentView("timeGridDay")
            }

            return (
              <Flex
                vertical
                className={"px-1 cursor-pointer"}
                gap={2}
                onClick={goToDay}
              >
                <Text
                  className={`uppercase tracking-wide text-[11px] ${isToday ? "text-inherit font-bold" : ""}`}
                  strong
                >
                  {date.format("ddd")}
                </Text>
                <Text
                  className={`text-base leading-none ${isToday ? "text-inherit font-bold" : "font-semibold"}`}
                >
                  {date.format("MMM D")}
                </Text>
              </Flex>
            )
          }}
          initialView={currentView}
          now={() => dayjs().tz(timezone).format()}
          initialDate={now}
          // locale={enGbLocale}
          // locales={[enGbLocale]}
          firstDay={0}
          droppable={false}
          editable={!!editAllowed}
          eventDurationEditable={false}
          //
          selectable={false}
          allDayText={"ALL DAY"}
          nowIndicator
          // FC's own line/arrow render nothing visible (see calendar.less) —
          // the full-width overlay rendered alongside this component's
          // return is what's actually painted, mirroring this line's
          // position. FC still needs `nowIndicator` on to lay that line out
          // at all, which is the only reason it's kept in the DOM.
          nowIndicatorContent={() => <></>}
          eventOverlap
          slotEventOverlap
          selectMirror
          dayMaxEvents={2} // Для dayGridMonth
          dayMaxEventRows={3} // Альтернатива для dayGrid
          eventMaxStack={3} // Максимум стеків events в одному слоті
          eventMinHeight={28} // fits the time + type-dot lines even on a 15-min slot
          weekends
          dragScroll={false}
          events={events}
          height="100%"
          timeZone={timezone}
          headerToolbar={false}
          slotMinTime="00:00:00"
          slotMaxTime="23:59:00"
          scrollTime={{ hours: 7 }}
          slotLabelFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          }}
          dayHeaderFormat={{
            weekday: "short",
            day: "numeric"
          }}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            meridiem: "short"
          }}
          eventDidMount={(info) => {
            const event = info.event
            const el = info.el

            // Примусово застосувати кольори з події
            if (event.backgroundColor) {
              el.style.backgroundColor = event.backgroundColor
            }
            if (event.borderColor) {
              el.style.borderColor = event.borderColor
            }
            if (event.textColor) {
              el.style.color = event.textColor
            }

            // Для тексту всередині події
            const titleEl = el.querySelector(".fc-event-title") as HTMLElement

            if (titleEl && event.textColor) {
              titleEl.style.color = event.textColor
            }

            const timeEl = el.querySelector(".fc-event-time") as HTMLElement
            if (timeEl && event.textColor) {
              timeEl.style.color = event.textColor
            }
          }}
          dateClick={createAllowed ? handleDateClick : undefined}
          moreLinkClick={handleMoreLinkClick}
          eventClick={handleEventClick}
          eventContent={CalendarEventCell}
          eventDragStart={startAlignedEventDrag.start}
          eventDragStop={startAlignedEventDrag.stop}
          eventDrop={onDropEvent}
          // eventResize={(info) => console.log("Event resized:", info)}
          // select={(props) => console.log("select:", props)}
        />
          </div>

          {isAvailabilityView && (
            <CalendarAvailability
              date={availabilityDate ?? dayjs().tz(timezone).toDate()}
              timezone={timezone}
              filters={filters}
              viewAllAllowed={viewAllAllowed}
              events={apiEvents}
              userId={userId}
              onSelectEvent={onSelectEvent}
            />
          )}
        </div>

        {!isMobile && (
          <CalendarSidebar
            events={apiEvents}
            timezone={timezone}
            userId={userId}
            filterParticipants={filters.participants}
            currentDate={
              isAvailabilityView
                ? (availabilityDate ?? dayjs().tz(timezone).toDate())
                : (getAPI()?.getDate() ?? dayjs().tz(timezone).toDate())
            }
            onSelectDate={(date) => {
              // FullCalendar's own `gotoDate` already resolves this per view:
              // in Day it lands on that day, in Week/Work Week it moves to
              // the week containing it, in Month it moves to that month —
              // exactly the "click a date -> navigate" behavior asked for,
              // with no per-view branching needed here. Availability isn't a
              // FullCalendar view, so it needs its own state kept in sync
              // directly — see the note by `availabilityDate` above for why
              // relying on `gotoDate` alone silently didn't update it.
              if (isAvailabilityView) setAvailabilityDate(date)
              getAPI()?.gotoDate(date)
            }}
            onSelectEvent={onSelectEvent}
            onViewSchedule={() => onViewChange("listWeek")}
          />
        )}
        </Flex>
      </Flex>
    )
  }
)

Calendar.displayName = "Calendar"
